/**
 * In-place model restart helpers: wipe / copy-from-template / AI draft → set-lines.
 * Always keeps recipe identity (code + name) on the target.
 */

import { buildCorrectedRecipeLinesPayload } from "@/lib/manufacturing/buildCorrectedRecipeLinesPayload";
import {
  duplicateLinesToSetPayload,
  duplicateRecipeDraftFromDetail,
} from "@/lib/manufacturing/duplicateRecipeDraft";
import { seedBomCorrectionDraft } from "@/lib/manufacturing/seedBomCorrectionDraft";
import type { DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";
import type { MfgRecipeDetailResponse } from "@/lib/manufacturing/types";
import {
  getManufacturingRecipeDetail,
  setManufacturingRecipeAttachments,
  setManufacturingRecipeLines,
  updateManufacturingRecipe,
} from "@/lib/manufacturing/recipesClient";

export async function wipeRecipeKeepingIdentity(recipeId: string): Promise<
  { ok: true } | { ok: false; error: string }
> {
  const linesRes = await setManufacturingRecipeLines({ recipe_id: recipeId, lines: [] });
  if (!linesRes.ok) return { ok: false, error: linesRes.error };

  const attachRes = await setManufacturingRecipeAttachments({
    parent_recipe_id: recipeId,
    attachments: [],
  });
  if (!attachRes.ok) return { ok: false, error: attachRes.error };

  const metaRes = await updateManufacturingRecipe({
    recipe_id: recipeId,
    notes: "",
    tags: [],
  });
  if (!metaRes.ok) return { ok: false, error: metaRes.error };
  return { ok: true };
}

export async function copyTemplateBomToRecipe(
  targetRecipeId: string,
  sourceRecipeId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const detail = await getManufacturingRecipeDetail(sourceRecipeId);
  if (!detail.ok) return { ok: false, error: detail.error };
  const draft = duplicateRecipeDraftFromDetail(detail.data);
  const lines = duplicateLinesToSetPayload(draft.lines);
  const linesRes = await setManufacturingRecipeLines({
    recipe_id: targetRecipeId,
    lines,
  });
  if (!linesRes.ok) return { ok: false, error: linesRes.error };

  const metaRes = await updateManufacturingRecipe({
    recipe_id: targetRecipeId,
    notes: draft.notes,
    tags: draft.tags,
  });
  if (!metaRes.ok) return { ok: false, error: metaRes.error };

  const attachRes = await setManufacturingRecipeAttachments({
    parent_recipe_id: targetRecipeId,
    attachments: [],
  });
  if (!attachRes.ok) return { ok: false, error: attachRes.error };
  return { ok: true };
}

/** Material-only full replace from an AI draft (drops prior component/use lines). */
export function buildRestartLinesFromDraft(
  draft: DictatedRecipeDraft,
  detail: MfgRecipeDetailResponse,
): { ok: true; lines: ReturnType<typeof duplicateLinesToSetPayload> } | { ok: false; error: string } {
  const built = buildCorrectedRecipeLinesPayload(draft, { ...detail, lines: [] });
  if (!built.ok) return built;
  return { ok: true, lines: built.lines };
}

export async function applyRestartDraftToRecipe(
  detail: MfgRecipeDetailResponse,
  draft: DictatedRecipeDraft,
  opts?: { notes?: string },
): Promise<{ ok: true } | { ok: false; error: string }> {
  const built = buildRestartLinesFromDraft(draft, detail);
  if (!built.ok) return built;
  const linesRes = await setManufacturingRecipeLines({
    recipe_id: detail.recipe.id,
    lines: built.lines,
  });
  if (!linesRes.ok) return { ok: false, error: linesRes.error };

  const metaRes = await updateManufacturingRecipe({
    recipe_id: detail.recipe.id,
    tags: Array.isArray(draft.tags) ? draft.tags : [],
    ...(opts?.notes !== undefined ? { notes: opts.notes } : {}),
  });
  if (!metaRes.ok) return { ok: false, error: metaRes.error };

  const attachRes = await setManufacturingRecipeAttachments({
    parent_recipe_id: detail.recipe.id,
    attachments: [],
  });
  if (!attachRes.ok) return { ok: false, error: attachRes.error };
  return { ok: true };
}

/** Lock identity fields onto a draft so AI output cannot rename the recipe. */
export function lockDraftIdentity(
  draft: DictatedRecipeDraft,
  detail: MfgRecipeDetailResponse,
): DictatedRecipeDraft {
  return {
    ...draft,
    code: detail.recipe.code,
    name: detail.recipe.name,
    kind: detail.recipe.kind ?? draft.kind,
  };
}

export function seedRestartDraftFromDetail(detail: MfgRecipeDetailResponse): DictatedRecipeDraft {
  return lockDraftIdentity(seedBomCorrectionDraft(detail), detail);
}
